import { useEffect, useState } from 'react'
import { Box, TextField, Typography, FormControl, Select, MenuItem, Button, Snackbar, Alert, Paper } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { Dayjs } from 'dayjs'

export type FieldType = 'text' | 'select' | 'date'

export interface FieldOption {
    value: string
    label: string
}

export interface FieldConfig {
    key: string
    label: string
    type: FieldType
    placeholder?: string
    options?: FieldOption[]
}

export interface FiltersPanelProps<T = Record<string, any>> {
    fields: FieldConfig[]
    initialValues?: T
    onApply: (values: T) => void
    showSnackbar?: boolean
}

const isEmptyValue = (v: any) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '')

function FiltersPanel<T extends Record<string, any> = Record<string, any>>({ fields, initialValues = {} as T, onApply, showSnackbar = true }: FiltersPanelProps<T>) {
    const [local, setLocal] = useState<T>({ ...(initialValues as T) })
    const [snackOpen, setSnackOpen] = useState(false)
    const [snackMessage, setSnackMessage] = useState('')

    useEffect(() => {
        setLocal({ ...(initialValues as T) })
    }, [initialValues])

    const handleChange = (key: string, value: any) => {
        setLocal(prev => ({ ...prev, [key]: value }))
    }

    const handleApply = () => {
        onApply(local)
        if (showSnackbar) {
            setSnackMessage('Filtros aplicados')
            setSnackOpen(true)
        }
    }

    const handleClear = () => {
        const cleared = fields.reduce((acc, f) => ({ ...acc, [f.key]: f.type === 'date' ? null : '' }), {} as T)
        setLocal(cleared)
        onApply(cleared)
        if (showSnackbar) {
            setSnackMessage('Filtros limpiados')
            setSnackOpen(true)
        }
    }

    const handleCloseSnack = (_?: any, reason?: string) => {
        if (reason === 'clickaway') return
        setSnackOpen(false)
    }

    const isDirty = fields.some(f => {
        const a = initialValues?.[f.key]
        const b = local?.[f.key]
        if (f.type === 'date') {
            return String(a) !== String(b)
        }
        return String(a || '') !== String(b || '')
    })

    const hasValues = fields.some(f => !isEmptyValue(local?.[f.key]))

    return (
        <Paper elevation={1} sx={{
            border: '1px solid #e5e7eb',
            borderRadius: 5,
            width: '100%',
            p: { xs: 2, sm: 3, md: 4, lg: 5 },
        }}>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(4, 1fr)'
                },
                gap: { xs: 2, sm: 2, md: 2, lg: 2 },
                alignItems: 'end'
            }} >
                {fields.map(field => (
                    <Box sx={{ justifyContent: 'center' }}
                        key={field.key}>
                        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            {field.label}
                        </Typography>
                        {field.type === 'text' && (
                            <TextField
                                fullWidth
                                size="small"
                                placeholder={field.placeholder}
                                value={local?.[field.key] ?? ''}
                                onChange={(e) => handleChange(field.key, e.target.value)}
                            />
                        )}

                        {field.type === 'select' && (
                            <FormControl fullWidth size="small">
                                <Select value={local?.[field.key] ?? ''} onChange={(e) => handleChange(field.key, e.target.value)}>
                                    {field.options?.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {field.type === 'date' && (
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                                <DatePicker
                                    value={local?.[field.key] ?? null}
                                    onChange={(d) => handleChange(field.key, d as Dayjs | null)}
                                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    format="DD-MM-YYYY"
                                />
                            </LocalizationProvider>
                        )}
                    </Box>
                ))}
            </Box>

            {(isDirty || hasValues) && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="outlined" size="small" onClick={handleClear} sx={{ textTransform: 'none' }}>
                            Limpiar filtros
                        </Button>
                        <Button variant="contained" size="small" onClick={handleApply} sx={{ textTransform: 'none' }}>
                            Aplicar filtros
                        </Button>
                    </Box>
                </Box>
            )}

            <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleCloseSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={handleCloseSnack} severity="success" sx={{ width: '100%' }}>
                    {snackMessage}
                </Alert>
            </Snackbar>
        </Paper>
    )
}

export default FiltersPanel
